import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid2";
import { 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    CircularProgress, 
    Card, 
    CardContent, 
    Typography, 
    Box,
    Chip,
    Divider,
    Paper
} from "@mui/material";
import { LineChart, Line as RechartsLine, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DataGrid } from "@mui/x-data-grid";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import RecentTrades from "../components/RecentTrades";
import Stats from "../components/Stats";
import { toast, Bounce } from "react-toastify";
import moment from "moment";
import { fetchTradesData, fetchUserAccounts, fetchUserAccountHistory } from "../services/api";
import { getBrokerFromLocalStorage, setBrokerInLocalStorage, getSelectedUserAccountFromLocalStorage, getSelectedUserAccountIdFromLocalStorage } from "../utils/data";
import { formatIndianCurrency, formatPercentage } from "../utils/currency";

const Analytics = () => {
    const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);

    const [recentTrades, setRecentTrades] = useState([]);
    const [recentTradeStats, setRecentTradeStats] = useState({});
    const [loading, setLoading] = useState(true);

    // Global user account state
    const [selectedAccount, setSelectedAccount] = useState(getSelectedUserAccountFromLocalStorage());
    const [userAccountHistory, setUserAccountHistory] = useState([]);
    const [userAccountLoading, setUserAccountLoading] = useState(false);

    useEffect(() => {
        fetchTrades();
        if (autoRefreshInterval == 0) {
            return;
        }
        const intervalId = setInterval(() => {
            fetchTrades();
        }, autoRefreshInterval * 1000);

        return () => {
            clearInterval(intervalId);
        }
    }, [autoRefreshInterval, selectedAccount]);

    useEffect(() => {
        if (selectedAccount?.id) {
            fetchUserHistory();
        }
    }, [selectedAccount]);

    // Listen for global user account changes
    useEffect(() => {
        const handleUserAccountChange = (event) => {
            setSelectedAccount(event.detail);
        };

        window.addEventListener('userAccountChanged', handleUserAccountChange);
        
        return () => {
            window.removeEventListener('userAccountChanged', handleUserAccountChange);
        };
    }, []);

    const showToast = (type, message) => {
        let properties = {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
            transition: Bounce,
        }
        switch (type) {
            case "success":
                toast.success(message, properties);
                break;
            case "error":
                toast.error(message, properties);
                break;
            case "warning":
                toast.warn(message, properties);
                break;
            default:
                toast.info(message, properties);
        }
    }

    // Fetch trades from API
    const fetchTrades = () => {
        if (!selectedAccount?.broker) {
            setLoading(false);
            return;
        }
        
        setLoading(true)
        let currentDate = moment().format("YYYY-MM-DD")
        fetchTradesData(selectedAccount.broker, currentDate)
            .then((response) => {
                const trades = response.data.trades;
                const stats = response.data.stats;
                setRecentTrades(trades.filter((trade) => trade.status === "closed"));
                setRecentTradeStats(stats);
                setLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching trades:", error);
                showToast("error", "Error fetching recent trades!");
                setLoading(false);
            });
    };

    // Fetch user account history
    const fetchUserHistory = () => {
        if (!selectedAccount?.id) {
            return;
        }
        
        setUserAccountLoading(true);
        fetchUserAccountHistory(selectedAccount.id)
            .then((response) => {
                const history = response.data?.user_account_history || [];
                setUserAccountHistory(history);
                setUserAccountLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching user account history:", error);
                showToast("error", "Error fetching user account history!");
                setUserAccountHistory([]);
                setUserAccountLoading(false);
            });
    };
    // Get latest user account state
    const latestAccountState = userAccountHistory.length > 0 ? userAccountHistory[userAccountHistory.length - 1] : null;

    // Prepare chart data for margin available
    const marginChartData = userAccountHistory.map(entry => ({
        timestamp: moment(entry.updated_at).format("HH:mm"),
        balance: entry.funds
    }));

    // Prepare chart data for net PnL
    const netPnlChartData = userAccountHistory.map(entry => ({
        timestamp: moment(entry.updated_at).format("HH:mm"),
        net_pnl: entry.net_pnl
    }));

    // Prepare chart data for margin blocked
    const marginBlockedChartData = userAccountHistory.map(entry => ({
        timestamp: moment(entry.updated_at).format("HH:mm"),
        margin_blocked: entry.margin_blocked
    }));

    // Enhanced Recent Trades table columns with auto-adjustable widths
    const enhancedTradesColumns = [
        { 
            field: "id", 
            headerName: "ID", 
            minWidth: 80,
            flex: 0.8,
            renderCell: (params) => (
                <Chip 
                    label={params.value} 
                    size="small" 
                    variant="outlined" 
                    color="primary"
                />
            )
        },
        { 
            field: "instrument_id", 
            headerName: "Instrument", 
            minWidth: 110,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight="medium">
                    {params.value}
                </Typography>
            )
        },
        { 
            field: "quantity", 
            headerName: "Quantity", 
            minWidth: 90,
            flex: 0.8,
            renderCell: (params) => (
                <Typography variant="body2" textAlign="center">
                    {params.value}
                </Typography>
            )
        },
        { 
            field: "buying_price", 
            headerName: "Buy Price", 
            minWidth: 100,
            flex: 1.2,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {formatIndianCurrency(params.value)}
                </Typography>
            )
        },
        { 
            field: "selling_price", 
            headerName: "Sell Price", 
            minWidth: 100,
            flex: 1.2,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {params.value ? formatIndianCurrency(params.value) : "-"}
                </Typography>
            )
        },
        {
            field: "net_pnl",
            headerName: "PnL",
            minWidth: 120,
            flex: 1.4,
            renderCell: (params) => {
                const isProfit = params.value >= 0;
                return (
                    <Box display="flex" alignItems="center" gap={0.5}>
                        {/* {isProfit ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="error" />} */}
                        <Typography 
                            variant="body2" 
                            fontWeight="medium"
                            color={isProfit ? "success.main" : "error.main"}
                        >
                            {formatIndianCurrency(params.value)}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: "net_pnl_percentage",
            headerName: "PnL %",
            minWidth: 100,
            flex: 1,
            renderCell: (params) => {
                const percentage = (100 * params.row.net_pnl / (params.row.quantity * params.row.buying_price));
                const isProfit = percentage >= 0;
                return (
                    <Chip 
                        label={`${percentage.toFixed(2)}%`}
                        size="small"
                        color={isProfit ? "success" : "error"}
                        variant={isProfit ? "filled" : "outlined"}
                    />
                );
            }
        },
        { 
            field: "buy_order_timestamp", 
            headerName: "Buy Time", 
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {params.value ? moment(params.value).format("HH:mm") : "-"}
                </Typography>
            )
        },
        { 
            field: "sell_order_timestamp", 
            headerName: "Sell Time", 
            minWidth: 100,
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {params.value ? moment(params.value).format("HH:mm") : "-"}
                </Typography>
            )
        }
    ];

    // Enhanced Stats Card Component
    const StatCard = ({ title, value, subtitle, icon, color = "primary", trend }) => (
        <Card elevation={2} sx={{ height: '100%', backgroundColor: 'white' }}>
            <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="body1" color="text.secondary">
                        <b>{title}</b>
                    </Typography>
                    <Box color={`${color}.main`}>
                        {icon}
                    </Box>
                </Box>
                <Typography variant="h5" fontWeight="bold" color={`${color}.main`}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {subtitle}
                    </Typography>
                )}
                {trend && (
                    <Box display="flex" alignItems="center" mt={1}>
                        {trend > 0 ? <TrendingUpIcon fontSize="small" color="success" /> : <TrendingDownIcon fontSize="small" color="error" />}
                        <Typography variant="caption" color={trend > 0 ? "success.main" : "error.main"} ml={0.5}>
                            {Math.abs(trend).toFixed(2)}%
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress size={40} />
            </Box>
        );
    }

    if (!selectedAccount) {
        return (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
                <AccountBalanceWalletIcon color="disabled" sx={{ fontSize: 64 }} />
                <Typography variant="h5" color="text.secondary">
                    No User Account Selected
                </Typography>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                    Please select a user account from the dropdown in the header to view analytics data.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Header Section */}
            {/* <Box mb={4}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Trading Analytics Dashboard
                </Typography>
                <Typography variant="subtitle1" color="text.secondary">
                    Comprehensive view of your trading performance and account status
                </Typography>
            </Box> */}

            {/* Account Info Section */}
            {/* {selectedAccount && (
                <Card elevation={1} sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" gap={2}>
                            <AccountBalanceWalletIcon color="primary" />
                            <Typography variant="h6" fontWeight="medium">
                                Active Account: {selectedAccount.name}
                            </Typography>
                            <Chip 
                                label={selectedAccount.broker || 'Unknown Broker'} 
                                size="medium" 
                                variant="outlined" 
                                color="primary"
                            />
                        </Box>
                    </CardContent>
                </Card>
            )} */}

            {/* Main Content Layout */}
            <Grid container spacing={3}>
                {/* Left Column - 60% Width - Trading Performance & Account Overview */}
                <Grid size={7}>
                    
                    {/* User Account Section */}
                    <Card elevation={2} sx={{ mb: 4 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={3}>
                                <AccountBalanceWalletIcon color="primary" />
                                <Typography variant="h5" fontWeight="bold">
                                    Account Overview
                                </Typography>
                            </Box>
                            
                            {latestAccountState && (
                                <Grid container spacing={2} mb={3}>
                                    <Grid size={3}>
                                        <StatCard
                                            title="Balance"
                                            value={formatIndianCurrency(latestAccountState.funds)}
                                            icon={<AccountBalanceWalletIcon />}
                                            color="primary"
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <StatCard
                                            title="Available Margin"
                                            value={formatIndianCurrency(latestAccountState.margin_available)}
                                            icon={<TrendingUpIcon />}
                                            color="success"
                                        />
                                    </Grid>
                                    <Grid size={3}>
                                        <StatCard
                                            title="Blocked Margin"
                                            value={formatIndianCurrency(latestAccountState.margin_blocked)}
                                            // icon={<TrendingDownIcon />}
                                            color="warning"
                                        />
                                    </Grid>
                                    {/* <Grid size={3}>
                                        <StatCard
                                            title="Net P&L"
                                            value={formatIndianCurrency(latestAccountState.net_pnl)}
                                            icon={latestAccountState.net_pnl >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
                                            color={latestAccountState.net_pnl >= 0 ? "success" : "error"}
                                        />
                                    </Grid> */}
                                    <Grid size={3}>
                                        <StatCard
                                            title="Stop Loss"
                                            value={formatIndianCurrency(latestAccountState.stop_loss)}
                                            icon={<TrendingDownIcon />}
                                            color="error"
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            
                            {/* Account History Charts */}
                            {userAccountHistory.length > 0 && (
                                <>
                                    {/* <Divider sx={{ my: 3 }} />
                                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                                        Account History Trends
                                    </Typography> */}
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid size={6}>
                                            <Card variant="outlined" elevation={1}>
                                                <CardContent>
                                                    <Typography variant="h6" align="center" gutterBottom color="primary">
                                                        Balance
                                                    </Typography>
                                                    <ResponsiveContainer width="100%" height={200}>
                                                        <LineChart data={marginChartData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="timestamp" />
                                                            <YAxis />
                                                            <Tooltip formatter={(value) => [formatIndianCurrency(value), 'Margin Available']} />
                                                            <Legend />
                                                            <RechartsLine 
                                                                type="monotone" 
                                                                dataKey="balance"
                                                                stroke="#1976d2" 
                                                                strokeWidth={3}
                                                                dot={{ fill: '#1976d2', strokeWidth: 2, r: 4 }}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                        {/* <Grid size={4}>
                                            <Card variant="outlined" elevation={1}>
                                                <CardContent>
                                                    <Typography variant="h6" align="center" gutterBottom color="warning.main">
                                                        Margin Blocked
                                                    </Typography>
                                                    <ResponsiveContainer width="100%" height={200}>
                                                        <LineChart data={marginBlockedChartData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="timestamp" />
                                                            <YAxis />
                                                            <Tooltip formatter={(value) => [formatIndianCurrency(value), 'Margin Blocked']} />
                                                            <Legend />
                                                            <RechartsLine 
                                                                type="monotone" 
                                                                dataKey="margin_blocked" 
                                                                stroke="#ff7300" 
                                                                strokeWidth={3}
                                                                dot={{ fill: '#ff7300', strokeWidth: 2, r: 4 }}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </CardContent>
                                            </Card>
                                        </Grid> */}
                                        <Grid size={6}>
                                            <Card variant="outlined" elevation={1}>
                                                <CardContent>
                                                    <Typography variant="h6" align="center" gutterBottom color="success.main">
                                                        Net PnL
                                                    </Typography>
                                                    <ResponsiveContainer width="100%" height={200}>
                                                        <LineChart data={netPnlChartData}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="timestamp" />
                                                            <YAxis />
                                                            <Tooltip formatter={(value) => [formatIndianCurrency(value), 'Net PnL']} />
                                                            <Legend />
                                                            <RechartsLine 
                                                                type="monotone" 
                                                                dataKey="net_pnl" 
                                                                stroke="#4caf50" 
                                                                strokeWidth={3}
                                                                dot={{ fill: '#4caf50', strokeWidth: 2, r: 4 }}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </CardContent>
                    </Card>
                    {/* Trading Statistics Section */}
                    <Card elevation={2} >
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={1} mb={3}>
                                <ShowChartIcon color="primary" />
                                <Typography variant="h5" fontWeight="bold">
                                    Trading Performance
                                </Typography>
                            </Box>
                            
                            <Grid container spacing={2}>
                                <Grid size={3}>
                                    <StatCard
                                        title="Total Trades"
                                        value={recentTradeStats.total_trades || 0}
                                        icon={<ShowChartIcon />}
                                        color="primary"
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <StatCard
                                        title="Gross Profit / Loss"
                                        value={formatIndianCurrency(recentTradeStats.gross_pnl || 0)}
                                        subtitle="Before charges"
                                        icon={<AccountBalanceWalletIcon />}
                                        color={recentTradeStats.gross_pnl >= 0 ? "success" : "error"}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <StatCard
                                        title="Total Charges"
                                        value={formatIndianCurrency(recentTradeStats.total_charges || 0)}
                                        subtitle="Brokerage + fees"
                                        icon={<AccountBalanceWalletIcon />}
                                        color="warning"
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <StatCard
                                        title="Net Profit / Loss"
                                        value={formatIndianCurrency(recentTradeStats.net_pnl || 0)}
                                        subtitle="After charges"
                                        icon={<AccountBalanceWalletIcon />}
                                        color={recentTradeStats.net_pnl >= 0 ? "success" : "error"}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <StatCard
                                        title="Win Rate"
                                        value={formatPercentage(recentTradeStats.win_percentage, false)}
                                        subtitle="Success percentage"
                                        icon={<TrendingUpIcon />}
                                        color={recentTradeStats.win_percentage >= 60 ? "success" : "error"}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <StatCard
                                        title="Profit Factor"
                                        value={(recentTradeStats.profit_factor || 0).toFixed(2)}
                                        subtitle="Total Profit / Loss Ratio"
                                        icon={<TrendingUpIcon />}
                                        color={recentTradeStats.profit_factor >= 1 ? "success" : "error"}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <StatCard
                                        title="Avg Profit %"
                                        value={formatPercentage(recentTradeStats.average_profit_percentage, false)}
                                        subtitle="Per winning trade"
                                        icon={<TrendingUpIcon />}
                                        color="primary"
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <StatCard
                                        title="Avg Loss %"
                                        value={formatPercentage(recentTradeStats.average_loss_percentage, false)}
                                        subtitle="Per losing trade"
                                        icon={<TrendingDownIcon />}
                                        color="primary"
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column - 40% Width - Recent Trades */}
                <Grid size={5}>
                    <Card elevation={2} sx={{ height: 'fit-content' }}>
                        <CardContent>
                            <Typography variant="h5" fontWeight="bold" gutterBottom>
                                Recent Trades
                            </Typography>
                            <Typography variant="subtitle2" color="text.secondary" mb={3}>
                                Latest trading activity with detailed P&L analysis
                            </Typography>
                            
                            <Paper elevation={0} sx={{ width: '100%' }}>
                                <DataGrid
                                    rows={recentTrades}
                                    columns={enhancedTradesColumns}
                                    initialState={{
                                        pagination: {
                                            paginationModel: { page: 0, pageSize: 20 }
                                        },
                                        sorting: {
                                            sortModel: [{ field: 'id', sort: 'desc' }]
                                        }
                                    }}
                                    pageSizeOptions={[10, 15, 20, 25, 50]}
                                    checkboxSelection={false}
                                    disableSelectionOnClick
                                    rowHeight={50}
                                    columnHeaderHeight={50}
                                    autoHeight={false}
                                    density="compact"
                                    sx={{
                                        border: 'none',
                                        '& .MuiDataGrid-root': {
                                            fontSize: '0.75rem',
                                        },
                                        '& .MuiDataGrid-cell': {
                                            borderBottom: '1px solid #f5f5f5',
                                            padding: '8px 6px',
                                            display: 'flex',
                                            alignItems: 'center',
                                        },
                                        '& .MuiDataGrid-columnHeaders': {
                                            backgroundColor: '#f8f9fa',
                                            borderBottom: '2px solid #e0e0e0',
                                            '& .MuiDataGrid-columnHeaderTitle': {
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                color: '#1976d2',
                                            }
                                        },
                                        '& .MuiDataGrid-columnHeader': {
                                            padding: '8px 6px',
                                        },
                                        '& .MuiDataGrid-row': {
                                            '&:hover': {
                                                backgroundColor: '#f8f9fa',
                                            },
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#fafafa',
                                            }
                                        },
                                        '& .MuiDataGrid-footerContainer': {
                                            borderTop: '2px solid #e0e0e0',
                                            backgroundColor: '#f8f9fa',
                                        },
                                        '& .MuiTablePagination-root': {
                                            fontSize: '0.75rem',
                                        },
                                        '& .MuiDataGrid-virtualScroller': {
                                            minHeight: '400px',
                                        }
                                    }}
                                />
                            </Paper>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Analytics;
